import React, { useEffect, useState, useCallback } from "react";
import API from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

const statusColors = {
  New: "bg-primary-subtle text-primary",
  InProgress: "bg-warning-subtle text-warning",
  OnHold: "bg-secondary-subtle text-secondary",
  Resolved: "bg-success-subtle text-success",
  Closed: "bg-dark-subtle text-dark",
  Escalated: "bg-danger-subtle text-danger",
};

const priorityColors = {
  LOW: "bg-success-subtle text-success",
  MEDIUM: "bg-warning-subtle text-warning",
  HIGH: "bg-warning-subtle text-warning",
  CRITICAL: "bg-danger-subtle text-danger",
};

const GrievancesView = () => {
  const { user } = useAuth();
  const [grievances, setGrievances] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetail, setShowDetail] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Forward
  const [showForward, setShowForward] = useState(false);
  const [forwardUserId, setForwardUserId] = useState("");
  const [forwardNote, setForwardNote] = useState("");
  const [forwardAction, setForwardAction] = useState("forward");
  const [forwarding, setForwarding] = useState(false);

  // Note
  const [showNote, setShowNote] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  // ── Fetch ─────────────────────────────────────────────────
  const fetchGrievances = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await API.get("/grievances/", {
          params: {
            page,
            per_page: 10,
            search: search || undefined,
            status: filterStatus || undefined,
          },
        });
        setGrievances(res.data.data.grievances);
        setTotalPages(res.data.data.pagination.total_pages);
        setTotalRecords(res.data.data.pagination.total);
        setCurrentPage(page);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [search, filterStatus],
  );

  const fetchAssignableUsers = useCallback(async () => {
    try {
      const res = await API.get("/grievances/assignable-users");
      setAssignableUsers(res.data.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchGrievances();
    fetchAssignableUsers();
  }, []);

  // ── Open Detail ───────────────────────────────────────────
  const openDetail = async (g) => {
    setDetailItem(g);
    setHistory([]);
    setShowDetail(true);
    setShowForward(false);
    setShowNote(false);
    try {
      // Fetch full grievance with documents
      const [detail, hist] = await Promise.all([
        API.get(`/grievances/${g.grievance_id}`),
        API.get(`/grievances/${g.grievance_id}/history`),
      ]);
      setDetailItem(detail.data.data);
      setHistory(hist.data.data);
    } catch (err) {
      console.error(err);
    }
  };

  // ── Update Status ─────────────────────────────────────────
  const updateStatus = async (grievance_id, status) => {
    try {
      await API.put(`/grievances/${grievance_id}`, { current_status: status });
      const res = await API.get(`/grievances/${grievance_id}`);
      setDetailItem(res.data.data);
      fetchGrievances(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error");
    }
  };

  // ── Add Note ──────────────────────────────────────────────
  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await API.post(`/grievances/${detailItem.grievance_id}/note`, {
        note: noteText,
      });
      setNoteText("");
      setShowNote(false);
      const res = await API.get(`/grievances/${detailItem.grievance_id}`);
      setDetailItem(res.data.data);
      fetchGrievances(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error");
    } finally {
      setAddingNote(false);
    }
  };

  // ── Forward ───────────────────────────────────────────────
  const handleForward = async (e) => {
    e.preventDefault();
    if (!forwardUserId) return alert("Please select a user");
    setForwarding(true);
    try {
      await API.post(`/grievances/${detailItem.grievance_id}/assign`, {
        to_user_id: parseInt(forwardUserId),
        note: forwardNote,
        action: forwardAction,
      });
      setShowForward(false);
      setForwardUserId("");
      setForwardNote("");
      const res = await API.get(`/grievances/${detailItem.grievance_id}`);
      setDetailItem(res.data.data);
      const hist = await API.get(
        `/grievances/${detailItem.grievance_id}/history`,
      );
      setHistory(hist.data.data);
      fetchGrievances(currentPage);
    } catch (err) {
      alert(err.response?.data?.message || "Error forwarding");
    } finally {
      setForwarding(false);
    }
  };

  // ── Allowed statuses by role ──────────────────────────────
  const allowedStatuses =
    user?.role === "Staff"
      ? ["New", "InProgress", "OnHold", "Resolved"]
      : ["New", "InProgress", "OnHold", "Resolved", "Closed"];

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-1">My Grievances</h4>
          <p className="text-muted small mb-0">
            Total <strong>{totalRecords}</strong> grievances assigned to you
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm rounded-4 mb-3">
        <div className="card-body p-3">
          <div className="row g-2">
            <div className="col-12 col-md-5">
              <input
                type="text"
                className="form-control"
                placeholder="Search by title, ticket no, name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-6 col-md-3">
              <select
                className="form-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All Status</option>
                {[
                  "New",
                  "InProgress",
                  "OnHold",
                  "Resolved",
                  "Closed",
                  "Escalated",
                ].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-2">
              <button
                className="btn btn-primary w-100"
                onClick={() => fetchGrievances(1)}
              >
                <i className="bi bi-search me-1"></i>Search
              </button>
            </div>
            <div className="col-6 col-md-2">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setSearch("");
                  setFilterStatus("");
                  fetchGrievances(1);
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : grievances.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-chat-left-text fs-1 d-block mb-2"></i>
              No grievances assigned to you.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="px-4 py-3 small text-muted fw-semibold">
                      #
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Ticket
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Title</th>
                    <th className="py-3 small text-muted fw-semibold">
                      Reporter
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Priority
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      Status
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Level</th>
                    <th className="py-3 small text-muted fw-semibold text-end pe-4">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {grievances.map((g, idx) => (
                    <tr key={g.grievance_id}>
                      <td className="px-4 text-muted small">
                        {(currentPage - 1) * 10 + idx + 1}
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border small fw-normal">
                          {g.ticket_no}
                        </span>
                      </td>
                      <td style={{ maxWidth: "200px" }}>
                        <div className="fw-semibold small text-truncate">
                          {g.title}
                        </div>
                        {g.category && (
                          <div
                            className="text-muted"
                            style={{ fontSize: "11px" }}
                          >
                            {g.category}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="small">{g.reporter_name || "—"}</div>
                        <div
                          className="text-muted"
                          style={{ fontSize: "11px" }}
                        >
                          {g.reporter_mobile || ""}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`badge small ${priorityColors[g.priority] || ""}`}
                        >
                          {g.priority}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge small ${statusColors[g.current_status] || ""}`}
                        >
                          {g.current_status}
                        </span>
                      </td>
                      <td>
                        <span className="small text-muted">
                          L{g.current_level}
                        </span>
                      </td>
                      <td className="text-end pe-4">
                        <button
                          className="btn btn-sm text-white"
                          style={{
                            background:
                              "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                          }}
                          onClick={() => openDetail(g)}
                        >
                          <i className="bi bi-eye me-1"></i>View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer bg-white border-0 d-flex justify-content-between align-items-center py-3 px-4">
            <span className="text-muted small">
              Page {currentPage} of {totalPages}
            </span>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchGrievances(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                  if (page > totalPages) return null;
                  return (
                    <li
                      key={page}
                      className={`page-item ${currentPage === page ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => fetchGrievances(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}
                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchGrievances(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetail && detailItem && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.6)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content border-0 rounded-4 shadow-lg">
              <div
                className="modal-header border-0 px-4 pt-4 pb-2"
                style={{
                  background: "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                  borderRadius: "1rem 1rem 0 0",
                }}
              >
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="badge bg-white text-dark small">
                      {detailItem.ticket_no}
                    </span>
                    <span
                      className={`badge small ${statusColors[detailItem.current_status]}`}
                    >
                      {detailItem.current_status}
                    </span>
                    <span
                      className={`badge small ${priorityColors[detailItem.priority]}`}
                    >
                      {detailItem.priority}
                    </span>
                    <span className="badge bg-white bg-opacity-25 text-white small">
                      Level {detailItem.current_level}
                    </span>
                  </div>
                  <h5 className="modal-title fw-bold text-white mb-0">
                    {detailItem.title}
                  </h5>
                </div>
                <button
                  className="btn-close btn-close-white"
                  onClick={() => setShowDetail(false)}
                ></button>
              </div>

              <div
                className="modal-body px-4 py-3"
                style={{ maxHeight: "70vh", overflowY: "auto" }}
              >
                <div className="row g-3">
                  {/* Left */}
                  <div className="col-12 col-lg-7">
                    {/* Reporter */}
                    <div className="card border-0 bg-light rounded-3 mb-3">
                      <div className="card-body p-3">
                        <h6 className="fw-semibold small mb-2">
                          <i className="bi bi-person me-1"></i>Reporter
                        </h6>
                        <div className="row g-2">
                          <div className="col-6">
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              Name
                            </div>
                            <div className="small fw-medium">
                              {detailItem.reporter_name || "—"}
                            </div>
                          </div>
                          <div className="col-6">
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              Mobile
                            </div>
                            <div className="small fw-medium">
                              {detailItem.reporter_mobile || "—"}
                            </div>
                          </div>
                          <div className="col-6">
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              EPIC
                            </div>
                            <div className="small fw-medium">
                              {detailItem.epic_no || "—"}
                            </div>
                          </div>
                          <div className="col-6">
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              Category
                            </div>
                            <div className="small fw-medium">
                              {detailItem.category || "—"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="card border-0 bg-light rounded-3 mb-3">
                      <div className="card-body p-3">
                        <h6 className="fw-semibold small mb-2">
                          <i className="bi bi-text-left me-1"></i>Description &
                          Notes
                        </h6>
                        <div
                          className="small text-muted"
                          style={{
                            whiteSpace: "pre-line",
                            maxHeight: "150px",
                            overflowY: "auto",
                          }}
                        >
                          {detailItem.description || "No description."}
                        </div>
                      </div>
                    </div>

                    {/* Documents */}
                    {detailItem.documents &&
                      detailItem.documents.length > 0 && (
                        <div className="card border-0 bg-light rounded-3 mb-3">
                          <div className="card-body p-3">
                            <h6 className="fw-semibold small mb-2">
                              <i className="bi bi-paperclip me-1"></i>
                              Attachments ({detailItem.documents.length})
                            </h6>
                            <div className="d-flex flex-wrap gap-2">
                              {detailItem.documents.map((doc) => (
                                <a
                                  key={doc.id}
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    const token =
                                      localStorage.getItem("access_token");
                                    fetch(
                                      `http://localhost:5000/api/grievances/download/${doc.id}`,
                                      {
                                        headers: {
                                          Authorization: `Bearer ${token}`,
                                        },
                                      },
                                    )
                                      .then((res) => res.blob())
                                      .then((blob) => {
                                        const url =
                                          window.URL.createObjectURL(blob);
                                        const a = document.createElement("a");
                                        a.href = url;
                                        a.download = doc.docname;
                                        a.click();
                                        window.URL.revokeObjectURL(url);
                                      });
                                  }}
                                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                >
                                  {doc.doctype === "PDF" ? (
                                    <i className="bi bi-file-earmark-pdf text-danger"></i>
                                  ) : (
                                    <i className="bi bi-image text-success"></i>
                                  )}
                                  <span
                                    style={{ maxWidth: "120px" }}
                                    className="text-truncate small"
                                  >
                                    {doc.docname}
                                  </span>
                                  <i className="bi bi-download ms-1"></i>
                                </a>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                    {/* History */}
                    <div className="card border-0 bg-light rounded-3">
                      <div className="card-body p-3">
                        <h6 className="fw-semibold small mb-2">
                          <i className="bi bi-clock-history me-1"></i>History
                        </h6>
                        {history.length === 0 ? (
                          <div className="text-muted small">
                            No history yet.
                          </div>
                        ) : (
                          history.map((h, idx) => (
                            <div
                              key={h.id}
                              className="d-flex gap-2 mb-2 pb-2 border-bottom"
                            >
                              <div
                                className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white flex-shrink-0"
                                style={{
                                  width: 26,
                                  height: 26,
                                  fontSize: "11px",
                                }}
                              >
                                {idx + 1}
                              </div>
                              <div>
                                <div className="small fw-medium">
                                  {h.from_user} →{" "}
                                  <span className="text-primary">
                                    {h.to_user}
                                  </span>
                                </div>
                                {h.reason && (
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: "11px" }}
                                  >
                                    {h.reason}
                                  </div>
                                )}
                                <div
                                  className="text-muted"
                                  style={{ fontSize: "10px" }}
                                >
                                  {h.assigned_at}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right — Actions */}
                  <div className="col-12 col-lg-5">
                    {/* Status Update */}
                    <div className="card border-0 bg-light rounded-3 mb-3">
                      <div className="card-body p-3">
                        <h6 className="fw-semibold small mb-2">
                          <i className="bi bi-arrow-repeat me-1"></i>Update
                          Status
                        </h6>
                        <div className="d-flex flex-wrap gap-2">
                          {allowedStatuses.map((s) => (
                            <button
                              key={s}
                              className={`btn btn-sm ${
                                detailItem.current_status === s
                                  ? "text-white"
                                  : "btn-outline-secondary"
                              }`}
                              style={
                                detailItem.current_status === s
                                  ? {
                                      background:
                                        "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                                    }
                                  : {}
                              }
                              onClick={() =>
                                updateStatus(detailItem.grievance_id, s)
                              }
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Add Note */}
                    <div className="card border-0 bg-light rounded-3 mb-3">
                      <div className="card-body p-3">
                        <h6 className="fw-semibold small mb-2">
                          <i className="bi bi-chat-square-text me-1"></i>Add
                          Note
                        </h6>
                        {showNote ? (
                          <form onSubmit={handleAddNote}>
                            <textarea
                              className="form-control form-control-sm mb-2"
                              rows={3}
                              placeholder="Type your note..."
                              value={noteText}
                              onChange={(e) => setNoteText(e.target.value)}
                              required
                            />
                            <div className="d-flex gap-2">
                              <button
                                type="submit"
                                className="btn btn-sm text-white"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                                }}
                                disabled={addingNote}
                              >
                                {addingNote ? (
                                  <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                  "Save Note"
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-light"
                                onClick={() => {
                                  setShowNote(false);
                                  setNoteText("");
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline-primary w-100"
                            onClick={() => setShowNote(true)}
                          >
                            <i className="bi bi-plus me-1"></i>Add Note
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Forward */}
                    <div className="card border-0 bg-light rounded-3">
                      <div className="card-body p-3">
                        <h6 className="fw-semibold small mb-2">
                          <i className="bi bi-arrow-right-circle me-1"></i>
                          Forward / Escalate
                        </h6>
                        {showForward ? (
                          <form onSubmit={handleForward}>
                            <div className="mb-2">
                              <select
                                className="form-select form-select-sm"
                                value={forwardAction}
                                onChange={(e) =>
                                  setForwardAction(e.target.value)
                                }
                              >
                                <option value="forward">
                                  ⬆️ Forward (Escalate)
                                </option>
                                <option value="assign">
                                  👤 Assign to someone
                                </option>
                              </select>
                            </div>
                            <div className="mb-2">
                              <select
                                className="form-select form-select-sm"
                                value={forwardUserId}
                                onChange={(e) =>
                                  setForwardUserId(e.target.value)
                                }
                                required
                              >
                                <option value="">Select user...</option>
                                {assignableUsers
                                  .filter((u) => u.id !== parseInt(user?.id))
                                  .map((u) => (
                                    <option key={u.id} value={u.id}>
                                      {u.name} ({u.role})
                                    </option>
                                  ))}
                              </select>
                            </div>
                            <div className="mb-2">
                              <textarea
                                className="form-control form-control-sm"
                                rows={2}
                                placeholder="Reason / Note (optional)"
                                value={forwardNote}
                                onChange={(e) => setForwardNote(e.target.value)}
                              />
                            </div>
                            <div className="d-flex gap-2">
                              <button
                                type="submit"
                                className="btn btn-sm text-white"
                                style={{
                                  background:
                                    "linear-gradient(135deg, #1a3c5e, #2d6a4f)",
                                }}
                                disabled={forwarding}
                              >
                                {forwarding ? (
                                  <span className="spinner-border spinner-border-sm"></span>
                                ) : forwardAction === "forward" ? (
                                  "⬆️ Forward"
                                ) : (
                                  "👤 Assign"
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-light"
                                onClick={() => {
                                  setShowForward(false);
                                  setForwardUserId("");
                                  setForwardNote("");
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline-warning w-100"
                            onClick={() => setShowForward(true)}
                          >
                            <i className="bi bi-arrow-right-circle me-1"></i>
                            Forward / Reassign
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer border-0 px-4 pb-3">
                <div className="text-muted small me-auto">
                  Created by <strong>{detailItem.created_by_name}</strong> on{" "}
                  {detailItem.created_at}
                </div>
                <button
                  className="btn btn-light px-4"
                  onClick={() => setShowDetail(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrievancesView;
